/**
 * Evaluator descriptions for tooltips and documentation
 * These provide concise one-liner explanations for each evaluator type
 */

export const EVALUATOR_DESCRIPTIONS = {
  // Azure AI Built-in Evaluators - Text Quality & Accuracy
  "similarity": "Measures semantic similarity between the AI response and expected ground truth using advanced language models.",
  "groundedness": "Verifies that the AI response is factually grounded in and supported by the provided context documents.",
  "relevance": "Evaluates how well the AI response addresses the specific query and stays on-topic with the provided context.",
  "coherence": "Assesses the logical flow, clarity, and internal consistency of the AI response for readability.",
  "fluency": "Measures language quality, grammar, and natural flow to ensure professional, well-written responses.",
  
  // Azure AI Built-in Evaluators - Statistical Metrics
  "rouge": "ROUGE-1 score measuring word overlap between response and ground truth, commonly used for summarization tasks.",
  "rouge_1": "ROUGE-1 score measuring word overlap between response and ground truth, commonly used for summarization tasks.",
  "rouge_precision": "ROUGE precision measuring exact word matches between response and ground truth.",
  "rouge_recall": "ROUGE recall measuring coverage of ground truth words in the response.",
  "rouge_f1_score": "ROUGE F1 score combining precision and recall for word overlap assessment.",
  "f1_score": "Classic precision-recall harmonic mean measuring accuracy when comparing response against ground truth.",
  "bleu": "BLEU score measuring n-gram overlap with ground truth, originally designed for translation quality assessment.",
  
  // Azure AI Built-in Evaluators - RAG-Specific
  "retrieval": "Evaluates the quality and relevance of documents retrieved by the RAG system for the given query.",
  "retrieval_quality": "Evaluates the quality and relevance of documents retrieved by the RAG system for the given query.",
  "qa": "Comprehensive question-answering evaluator specifically designed for assessing Q&A system performance.",
  "question_answering": "Comprehensive question-answering evaluator specifically designed for assessing Q&A system performance.",
  
  // Custom Domain-Specific Evaluators
  "numerical_correctness": "Validates accuracy of numerical values, calculations, and measurements in responses about building regulations.",
  "numericcorrectness": "Validates accuracy of numerical values, calculations, and measurements in responses about building regulations.",
  "numeric_correctness": "Validates accuracy of numerical values, calculations, and measurements in responses about building regulations.",
  "citation_accuracy": "Checks that responses properly cite and reference relevant building codes, regulations, and official sources.",
  "citationaccuracy": "Checks that responses properly cite and reference relevant building codes, regulations, and official sources.",
  "regulatory_compliance": "Ensures responses align with current building regulations, planning laws, and compliance requirements.",
  "regulatorycompliance": "Ensures responses align with current building regulations, planning laws, and compliance requirements.",
  "technical_terminology": "Verifies correct usage of architectural, engineering, and building industry terminology and definitions.",
  "technicalterminology": "Verifies correct usage of architectural, engineering, and building industry terminology and definitions.",
  "content_filtering": "Detects inappropriate content and ensures the system returns proper security responses when needed.",
  "contentfiltering": "Detects inappropriate content and ensures the system returns proper security responses when needed.",
};

/**
 * Get description for an evaluator, handling various name formats
 * @param {string} evaluatorName - The evaluator name/ID
 * @returns {string|null} Description or null if not found
 */
export function getEvaluatorDescription(evaluatorName) {
  if (!evaluatorName || typeof evaluatorName !== 'string') {
    return null;
  }
  
  // Normalize the name (lowercase, replace spaces/dashes with underscores)
  const normalized = evaluatorName.toLowerCase().trim()
    .replace(/[\s\-.]+/g, '_')
    .replace(/_+/g, '_');
  
  // Try exact match first
  if (EVALUATOR_DESCRIPTIONS[normalized]) {
    return EVALUATOR_DESCRIPTIONS[normalized];
  }
  
  // Try original name
  if (EVALUATOR_DESCRIPTIONS[evaluatorName]) {
    return EVALUATOR_DESCRIPTIONS[evaluatorName];
  }
  
  // Try without underscores
  const withoutUnderscores = normalized.replace(/_/g, '');
  if (EVALUATOR_DESCRIPTIONS[withoutUnderscores]) {
    return EVALUATOR_DESCRIPTIONS[withoutUnderscores];
  }
  
  return null;
}

/**
 * Get evaluator category based on name
 * @param {string} evaluatorName - The evaluator name/ID
 * @returns {string} Category name
 */
export function getEvaluatorCategory(evaluatorName) {
  if (!evaluatorName || typeof evaluatorName !== 'string') {
    return 'Other';
  }
  
  const normalized = evaluatorName.toLowerCase().trim();
  
  // Text Quality & Accuracy
  if (['similarity', 'groundedness', 'relevance', 'coherence', 'fluency'].includes(normalized)) {
    return 'Text Quality & Accuracy';
  }
  
  // Statistical Metrics
  if (normalized.includes('rouge') || normalized.includes('bleu') || normalized.includes('f1')) {
    return 'Statistical Metrics';
  }
  
  // RAG-Specific
  if (normalized.includes('retrieval') || normalized.includes('qa') || normalized.includes('question')) {
    return 'RAG-Specific';
  }
  
  // Custom Domain-Specific
  if (['numerical_correctness', 'citation_accuracy', 'regulatory_compliance', 'technical_terminology', 'content_filtering'].some(custom => 
    normalized.includes(custom.replace('_', '')) || normalized.includes(custom)
  )) {
    return 'Custom Domain-Specific';
  }
  
  return 'Other';
}
